import React, { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Form, Loader, Table }             from 'semantic-ui-react';
import type { DropdownItemProps }                            from 'semantic-ui-react';
import { useCentralCalls }                                   from '@src/rest/CentralCalls';
import { parseArrayPayload }                                 from './offeringHoursData';
import type { Company, Consultant }                          from './consultantDashModel';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
] as const;

type MonthName = typeof MONTHS[ number ];

type RowType = 'Detail' | 'Subtotal' | 'Total';

/** One row of tng.sp_get_ClientConsultantMonthlyHours. */
type MonthlyHoursRow = Record<MonthName, number> & {
    RowType: RowType;
    ClientId: string | null;
    ClientName: string | null;
    ConsultantId: string | null;
    ConsultantName: string | null;
    TotalHours: number;
    BillableHours: number;
    CostPerHour: number | null;
    CostOfHours: number;
};

interface ClientGroup {
    clientId: string;
    clientName: string;
    details: MonthlyHoursRow[];
    subtotal?: MonthlyHoursRow;
}

const YEARS_BACK = 5;

const hoursFormat = new Intl.NumberFormat( undefined, { maximumFractionDigits: 2 } );
const currencyFormat = new Intl.NumberFormat( undefined, { style: 'currency', currency: 'USD' } );

const formatHours = ( value: number | null | undefined ) => hoursFormat.format( Number( value ) || 0 );
const formatCurrency = ( value: number | null | undefined ) => currencyFormat.format( Number( value ) || 0 );

interface FinanceHourReportsProps {
    clients: Company[];
    consultants: Consultant[];
}

/**
 * Finance view: hours per client and consultant for each month of a year, with client
 * subtotals, a grand total, billable hours (travel at 50%) and cost at the default rate.
 */
const FinanceHourReports: FC<FinanceHourReportsProps> = ( { clients, consultants } ) => {
    const { executeStoredProcedure } = useCentralCalls();

    const currentYear = new Date().getFullYear();

    const [ year, setYear ]                           = useState( currentYear ),
          [ clientId, setClientId ]                   = useState( '' ),
          [ consultantId, setConsultantId ]           = useState( '' ),
          [ hideEmptyMonths, setHideEmptyMonths ]     = useState( false ),
          [ rows, setRows ]                           = useState<MonthlyHoursRow[]>( [] ),
          [ loading, setLoading ]                     = useState( false ),
          [ error, setError ]                         = useState( '' ),
          [ reloadKey, setReloadKey ]                 = useState( 0 );

    useEffect( () => {
        let cancelled = false;

        const load = async () => {
            setLoading( true );
            setError( '' );
            try {
                const response = await executeStoredProcedure( 'tng.sp_get_ClientConsultantMonthlyHours', {
                    year,
                    ...( clientId ? { clientId } : {} ),
                    ...( consultantId ? { consultantId } : {} ),
                } );
                if( !cancelled ) setRows( parseArrayPayload( response ) as MonthlyHoursRow[] );
            } catch( err ){
                console.error( '[FinanceHourReports] failed to load hours:', err );
                if( !cancelled ){
                    setRows( [] );
                    setError( 'Could not load the hours report.' );
                }
            } finally {
                if( !cancelled ) setLoading( false );
            }
        };

        load();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ year, clientId, consultantId, reloadKey ] );

    const yearOptions: DropdownItemProps[] = useMemo(
        () => Array.from( { length: YEARS_BACK + 1 }, ( _, i ) => currentYear - i )
            .map( y => ( { key: y, value: y, text: String( y ) } ) ),
        [ currentYear ]
    );

    const clientOptions: DropdownItemProps[] = useMemo(
        () => [ { key: 'all', value: '', text: 'All clients' } ].concat(
            [ ...clients ]
                .sort( ( a, b ) => a.name.localeCompare( b.name ) )
                .map( c => ( { key: c.id, value: c.id, text: c.name } ) )
        ),
        [ clients ]
    );

    const consultantOptions: DropdownItemProps[] = useMemo(
        () => [ { key: 'all', value: '', text: 'All consultants' } ].concat(
            [ ...consultants ]
                .sort( ( a, b ) => a.name.localeCompare( b.name ) )
                .map( c => ( { key: c.id, value: c.id, text: c.name } ) )
        ),
        [ consultants ]
    );

    const { groups, total } = useMemo( () => {
        const byClient = new Map<string, ClientGroup>();
        let grandTotal: MonthlyHoursRow | undefined;

        rows.forEach( row => {
            if( row.RowType === 'Total' ){
                grandTotal = row;
                return;
            }

            const key = row.ClientId ?? '';
            let group = byClient.get( key );
            if( !group ){
                group = { clientId: key, clientName: row.ClientName ?? 'Unknown client', details: [] };
                byClient.set( key, group );
            }

            if( row.RowType === 'Subtotal' ) group.subtotal = row;
            else group.details.push( row );
        } );

        return { groups: Array.from( byClient.values() ), total: grandTotal };
    }, [ rows ] );

    const visibleMonths: MonthName[] = useMemo(
        () => hideEmptyMonths && total
            ? MONTHS.filter( month => Number( total[ month ] ) !== 0 )
            : [ ...MONTHS ],
        [ hideEmptyMonths, total ]
    );

    const columnCount = visibleMonths.length + 5;

    const renderHoursCells = ( row: MonthlyHoursRow ) => (
        <>
            { visibleMonths.map( month => (
                <Table.Cell key={ month } textAlign={ 'right' } className={ Number( row[ month ] ) === 0 ? 'finance-hours-zero' : undefined }>
                    { formatHours( row[ month ] ) }
                </Table.Cell>
            ) ) }
            <Table.Cell textAlign={ 'right' } className={ 'finance-hours-total' }>{ formatHours( row.TotalHours ) }</Table.Cell>
            <Table.Cell textAlign={ 'right' }>{ formatHours( row.BillableHours ) }</Table.Cell>
        </>
    );

    return (
        <div className={ 'finance-hour-reports' }>
            <div className={ 'finance-hour-reports-header' }>
                <h2 className={ 'finance-hour-reports-title' }>Finance — Hours by Client &amp; Consultant</h2>
                <Button
                    basic
                    size={ 'small' }
                    icon={ 'refresh' }
                    content={ 'Refresh' }
                    loading={ loading }
                    disabled={ loading }
                    onClick={ () => setReloadKey( key => key + 1 ) }
                />
            </div>

            <Form className={ 'finance-hour-reports-filters' }>
                <Form.Group widths={ 'equal' }>
                    <Form.Dropdown
                        label={ 'Year' }
                        selection
                        options={ yearOptions }
                        value={ year }
                        onChange={ ( _e, d ) => setYear( Number( d.value ) || currentYear ) }
                    />
                    <Form.Dropdown
                        label={ 'Client' }
                        selection
                        search
                        options={ clientOptions }
                        value={ clientId }
                        onChange={ ( _e, d ) => setClientId( String( d.value ?? '' ) ) }
                    />
                    <Form.Dropdown
                        label={ 'Consultant' }
                        selection
                        search
                        options={ consultantOptions }
                        value={ consultantId }
                        onChange={ ( _e, d ) => setConsultantId( String( d.value ?? '' ) ) }
                    />
                </Form.Group>
                <Form.Field>
                    <Checkbox
                        toggle
                        label={ 'Hide months with no hours' }
                        checked={ hideEmptyMonths }
                        onChange={ ( _e, d ) => setHideEmptyMonths( Boolean( d.checked ) ) }
                    />
                </Form.Field>
            </Form>

            <div className={ 'finance-hour-reports-stats' }>
                <div className={ 'finance-stat' }>
                    <span className={ 'finance-stat-label' }>Total hours</span>
                    <span className={ 'finance-stat-value' }>{ formatHours( total?.TotalHours ) }</span>
                </div>
                <div className={ 'finance-stat' }>
                    <span className={ 'finance-stat-label' }>Billable hours (travel @ 50%)</span>
                    <span className={ 'finance-stat-value' }>{ formatHours( total?.BillableHours ) }</span>
                </div>
                <div className={ 'finance-stat' }>
                    <span className={ 'finance-stat-label' }>Cost of hours</span>
                    <span className={ 'finance-stat-value' }>{ formatCurrency( total?.CostOfHours ) }</span>
                </div>
                <div className={ 'finance-stat' }>
                    <span className={ 'finance-stat-label' }>Clients</span>
                    <span className={ 'finance-stat-value' }>{ groups.length }</span>
                </div>
            </div>

            { error && <p className={ 'consultant-widget-empty' }>{ error }</p> }

            { loading && rows.length === 0 ? (
                <Loader active inline={ 'centered' }/>
            ) : !error && groups.length === 0 ? (
                <p className={ 'consultant-widget-empty' }>No hours logged for { year } with these filters.</p>
            ) : groups.length > 0 && (
                <div className={ 'finance-hour-reports-table-wrap' }>
                    <Table compact celled unstackable className={ 'consultant-table finance-hour-reports-table' }>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell className={ 'finance-sticky-col' }>Client / Consultant</Table.HeaderCell>
                                { visibleMonths.map( month => (
                                    <Table.HeaderCell key={ month } textAlign={ 'right' }>{ month.slice( 0, 3 ) }</Table.HeaderCell>
                                ) ) }
                                <Table.HeaderCell textAlign={ 'right' }>Total Hours</Table.HeaderCell>
                                <Table.HeaderCell textAlign={ 'right' }>Billable Hours</Table.HeaderCell>
                                <Table.HeaderCell textAlign={ 'right' }>Cost $/hr</Table.HeaderCell>
                                <Table.HeaderCell textAlign={ 'right' }>Cost of Hours</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>

                        <Table.Body>
                            { groups.map( group => (
                                <Fragment key={ group.clientId }>
                                    <Table.Row className={ 'finance-client-row' }>
                                        <Table.Cell colSpan={ columnCount } className={ 'finance-sticky-col' }>
                                            { group.clientName }
                                        </Table.Cell>
                                    </Table.Row>

                                    { group.details.map( row => (
                                        <Table.Row key={ `${ group.clientId }-${ row.ConsultantId }` }>
                                            <Table.Cell className={ 'finance-sticky-col finance-consultant-cell' }>
                                                { row.ConsultantName || 'Unknown consultant' }
                                            </Table.Cell>
                                            { renderHoursCells( row ) }
                                            <Table.Cell textAlign={ 'right' }>{ formatCurrency( row.CostPerHour ) }</Table.Cell>
                                            <Table.Cell textAlign={ 'right' }>{ formatCurrency( row.CostOfHours ) }</Table.Cell>
                                        </Table.Row>
                                    ) ) }

                                    { group.subtotal && (
                                        <Table.Row className={ 'finance-subtotal-row' }>
                                            <Table.Cell className={ 'finance-sticky-col' }>{ group.clientName } Subtotal</Table.Cell>
                                            { renderHoursCells( group.subtotal ) }
                                            <Table.Cell/>
                                            <Table.Cell textAlign={ 'right' }>{ formatCurrency( group.subtotal.CostOfHours ) }</Table.Cell>
                                        </Table.Row>
                                    ) }
                                </Fragment>
                            ) ) }
                        </Table.Body>

                        { total && (
                            <Table.Footer>
                                <Table.Row className={ 'finance-total-row' }>
                                    <Table.HeaderCell className={ 'finance-sticky-col' }>Grand Total</Table.HeaderCell>
                                    { visibleMonths.map( month => (
                                        <Table.HeaderCell key={ month } textAlign={ 'right' }>{ formatHours( total[ month ] ) }</Table.HeaderCell>
                                    ) ) }
                                    <Table.HeaderCell textAlign={ 'right' }>{ formatHours( total.TotalHours ) }</Table.HeaderCell>
                                    <Table.HeaderCell textAlign={ 'right' }>{ formatHours( total.BillableHours ) }</Table.HeaderCell>
                                    <Table.HeaderCell/>
                                    <Table.HeaderCell textAlign={ 'right' }>{ formatCurrency( total.CostOfHours ) }</Table.HeaderCell>
                                </Table.Row>
                            </Table.Footer>
                        ) }
                    </Table>
                </div>
            ) }
        </div>
    );
};

export default FinanceHourReports;
